import { useState } from 'react';

export default function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How do I set up ZALETELJ VPN on my device?',
      answer: 'Setting up ZALETELJ VPN is simple. Download the app for your device from our website, install it, create an account or log in, and click the connect button. The app will automatically configure the best settings for your connection.'
    },
    {
      question: 'What devices are supported?',
      answer: 'ZALETELJ VPN supports Windows, macOS, iOS, Android, and Linux. You can use one account to protect multiple devices simultaneously, ensuring all your devices are secure.'
    },
    {
      question: 'Is my data really private with ZALETELJ VPN?',
      answer: 'Absolutely. We maintain a strict zero-logs policy, meaning we do not track, collect, or store any information about your online activities. Your data is encrypted with military-grade AES-256 encryption, ensuring complete privacy.'
    },
    {
      question: 'Will using a VPN slow down my internet speed?',
      answer: 'ZALETELJ VPN is optimized for speed. While encryption may cause a minimal decrease in speed, our advanced infrastructure and WireGuard protocol ensure you experience fast, reliable connections suitable for streaming, gaming, and browsing.'
    },
    {
      question: 'Can I use ZALETELJ VPN for streaming?',
      answer: 'Yes! ZALETELJ VPN works seamlessly with popular streaming services. Our high-speed servers are optimized for streaming, allowing you to access content from different regions without buffering.'
    },
    {
      question: 'What is the kill switch feature?',
      answer: 'The kill switch is a safety feature that automatically disconnects your internet if the VPN connection drops unexpectedly. This prevents your real IP address and data from being exposed, ensuring continuous protection.'
    },
    {
      question: 'How do I change my VPN server location?',
      answer: 'Simply open the ZALETELJ VPN app, browse the list of available server locations, and click on your preferred country or city. The app will connect you to the optimal server in that location within seconds.'
    },
    {
      question: 'What should I do if I cannot connect to the VPN?',
      answer: 'First, check your internet connection. Try switching to a different server location or protocol in the app settings. If issues persist, restart the app or your device. Contact our 24/7 support team if you need further assistance.'
    }
  ];

  const guides = [
    {
      icon: 'ri-download-line',
      title: 'Installation Guide',
      description: 'Step-by-step instructions for installing ZALETELJ VPN on all supported platforms.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Configuration Tips',
      description: 'Optimize your VPN settings for the best performance and security.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security Best Practices',
      description: 'Learn how to maximize your online privacy and security with our VPN.'
    },
    {
      icon: 'ri-question-line',
      title: 'Troubleshooting',
      description: 'Common issues and solutions to help you resolve problems quickly.'
    }
  ];

  return (
    <section id="support" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-1.5 bg-purple-100 rounded-full">
            <span className="text-purple-600 text-xs font-semibold uppercase tracking-wider">We're Here to Help</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Support Center
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions, access helpful guides, and get the support you need to make the most of ZALETELJ VPN.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {guides.map((guide, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                <i className={`${guide.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{guide.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{guide.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 rounded-3xl p-10 md:p-12">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">
            Frequently Asked Questions
          </h3>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-cyan-600 transition-transform flex-shrink-0 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-3xl p-10 text-center shadow-xl">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Still Need Help?
          </h3>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Our support team is available 24/7 to assist you with any questions or technical issues you may have.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-3 bg-white text-cyan-600 font-bold rounded-xl hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Contact Support
            </button>
            <a
              href="mailto:information@zaleteljdoo.com"
              className="px-8 py-3 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-cyan-600 transition-all whitespace-nowrap"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
